import { makeAutoObservable } from 'mobx';
import {
  Object3D,
  Mesh,
  SkinnedMesh,
  MeshStandardMaterial,
  Vector3,
  Box3,
  PerspectiveCamera,
  Bone,
  Intersection,
  CanvasTexture,
  PointLight,
  Raycaster,
  Vector2,
  Color,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { sceneStore } from '../../../stores';

import modelPath from '../../../assets/models/scene.glb';

export class BodyProcessingModel {
  public modelData: Object3D | null = null;
  private isLoading = false;

  public boneMap: Record<string, Bone[]> = {};
  public muscleMap: Record<string, (Mesh | SkinnedMesh)[]> = {};
  public meshNames: string[] = [];

  private selectedObject: Mesh | SkinnedMesh | null = null;
  private raycaster = new Raycaster();

  constructor(private sceneStoreInstance = sceneStore) {
    makeAutoObservable(this);
  }

  init(_lights: PointLight[] = []) {
    if (this.modelData || this.isLoading) return;
    this.isLoading = true;

    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/js/libs/draco-new/');
    loader.setDRACOLoader(dracoLoader);

    loader.load(modelPath, (gltf) => {
      this.modelData = gltf.scene;
      this.isLoading = false;

      if (!this.sceneStoreInstance.scene.children.includes(this.modelData)) {
        this.sceneStoreInstance.scene.add(this.modelData);
      }

      // ---------------- BoundingBox ----------------
      const box = new Box3().setFromObject(this.modelData);
      const size = new Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      const scaleFactor = 1 / maxDim;

      const center = box.getCenter(new Vector3());
      this.modelData.position.sub(center.multiplyScalar(scaleFactor));
      this.modelData.scale.setScalar(scaleFactor);

// 🔹 Поднять модель по оси Y (например, на 0.2)
      this.modelData.position.y += 0.2;

// 🔹 Развернуть по Y на 180°
      this.modelData.rotation.y = Math.PI;
      this.modelData.rotation.x = -Math.PI/18;
      // ---------------- Материалы ----------------
      this.modelData.traverse((child) => {
        if (child instanceof Mesh || child instanceof SkinnedMesh) {
          const lowerName = child.name.toLowerCase();

          let material: MeshStandardMaterial;

          if (lowerName.includes('голова') || lowerName.includes('тело')) {
            // телесный с лёгким свечением
            material = new MeshStandardMaterial({
              color: new Color(0xffc8a2), // персиковый
              roughness: 0.5,
              metalness: 0.1,
              emissive: new Color(0xffb07c),
              emissiveIntensity: 0.2,
            });
          } else if (lowerName.includes('сустав') || lowerName.includes('позвоночник')) {
            // фиолетовый металл + подсветка
            material = new MeshStandardMaterial({
              color: new Color(0x9932cc),
              roughness: 0.3,
              metalness: 0.6,
              emissive: new Color(0x7a1fa2),
              emissiveIntensity: 0.5,
            });
          } else {
            // мышцы → яркие оттенки с мягким свечением
            const hue = Math.random() * 60 + 340; // розово-красный диапазон
            material = new MeshStandardMaterial({
              color: new Color(0xffc8a2), // персиковый
              roughness: 0.5,
              metalness: 0.1,
              emissive: new Color(0xffb07c),
              emissiveIntensity: 0.2,
            });
          }

          if (Array.isArray(child.material)) {
            child.material = child.material.map(() => material);
          } else {
            child.material = material;
          }

          child.castShadow = true;
          child.receiveShadow = true;
          child.visible = true;
        }
      });

      // ---------------- Подсветка ----------------
      const keyLight = new PointLight(0xffffff, 1.2, 100);
      keyLight.position.set(5, 10, 10);
      this.sceneStoreInstance.scene.add(keyLight);

      const fillLight = new PointLight(0x448aff, 0.6, 80); // голубая подсветка
      fillLight.position.set(-8, 5, -5);
      this.sceneStoreInstance.scene.add(fillLight);

      const rimLight = new PointLight(0xff4081, 0.8, 80); // розовый контурный свет
      rimLight.position.set(0, 8, -10);
      this.sceneStoreInstance.scene.add(rimLight);


      // ---------------- Камера ----------------
      const camera = this.sceneStoreInstance.camera as PerspectiveCamera;
      const cameraDistance = size.length() * scaleFactor * 1.5;
      camera.position.set(0, cameraDistance, cameraDistance);
      camera.lookAt(0, 0, 0);
      this.sceneStoreInstance.controls.target.set(0, 0, 0);
      this.sceneStoreInstance.controls.update();

      // ---------------- Кэш костей и мешей ----------------
      this.buildBoneCache();
      this.buildMuscleCache();

      console.log('✅ Модель загружена', size, center, 'Scale:', scaleFactor);
    });
  }

  private buildBoneCache() {
    if (!this.modelData) return;
    const bones: Bone[] = [];
    this.modelData.traverse((obj) => {
      if (obj instanceof Bone) bones.push(obj);
    });
    bones.forEach((bone) => {
      if (!this.boneMap[bone.name]) this.boneMap[bone.name] = [];
      this.boneMap[bone.name].push(bone);
    });
    console.log(`✅ Найдено костей: ${bones.length}`);
  }

  private buildMuscleCache() {
    if (!this.modelData) return;
    const meshes: (Mesh | SkinnedMesh)[] = [];
    this.modelData.traverse((obj) => {
      if ((obj as any).isMesh || (obj as any).isSkinnedMesh) meshes.push(obj as Mesh | SkinnedMesh);
    });

    meshes.forEach((mesh) => {
      const name = mesh.name.toLowerCase();
      if (!this.muscleMap[name]) this.muscleMap[name] = [];
      this.muscleMap[name].push(mesh);

      // Корректное добавление имени мышцы, игнорируя Scene
      const baseName =
          mesh.parent && mesh.parent.type !== 'Scene'
              ? this.normalizeName(mesh.parent.name)
              : this.normalizeName(mesh.name);

      if (!this.meshNames.includes(baseName)) this.meshNames.push(baseName);
    });

    console.log(`✅ Найдено мышц (мешей): ${meshes.length}`);
  }

  private normalizeName(name: string): string {
    return name.trim().toLowerCase();
  }

  /** Очистка ресурсов */
  public clear() {
    const scene = this.sceneStoreInstance.scene;
    if (!scene) return;

    function disposeMeshes(obj: Object3D | undefined) {
      if (!obj) return;
      obj.children.forEach((child) => {
        disposeMeshes(child);
        if (child instanceof Mesh || child instanceof SkinnedMesh) {
          child.parent?.remove(child);
          child.geometry?.dispose();
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach((mat) => {
            if (!mat) return;
            const tex = (mat as any).map;
            if (tex && !(tex instanceof CanvasTexture)) tex.dispose();
            mat.dispose();
          });
        }
      });
    }

    disposeMeshes(scene);

    if (this.sceneStoreInstance.outlinePass) {
      this.sceneStoreInstance.outlinePass.dispose();
    }

    this.modelData = null;
  }

  /** Выделение / hover */
  public handleClick(intersects: Intersection[]) {
    this.clearSelection();
    if (!intersects.length) return;
    intersects.sort((a, b) => a.distance - b.distance);
    const selected = intersects[0].object as Mesh | SkinnedMesh;
    this.selectObject(selected);
  }

  public selectObject(mesh: Mesh | SkinnedMesh) {
    this.clearSelection();
    this.selectedObject = mesh;
    this.sceneStoreInstance.outlinePass?.selectedObjects.push(mesh);
  }

  public clearSelection() {
    if (this.selectedObject) {
      if (this.sceneStoreInstance.outlinePass) {
        this.sceneStoreInstance.outlinePass.selectedObjects = [];
      }
      this.selectedObject = null;
    }
  }

  /** Ховер через Raycaster */
  public checkIntersection(mouse: Vector2): string | null {
    if (!this.modelData) return null;

    this.raycaster.setFromCamera(mouse, this.sceneStoreInstance.camera);

    const intersects = this.raycaster.intersectObject(this.modelData, true);
    if (intersects.length > 0) {
      const first = intersects[0].object as Mesh | SkinnedMesh;
      return first.name || null;
    }

    return null;
  }

  /** Применение кадра анимации */
  public applyFrame(frame: Record<string, any>) {
    for (const name in frame) {
      const meshes = this.muscleMap[name.toLowerCase()] || [];
      meshes.forEach((mesh) => {
        if (mesh instanceof SkinnedMesh && mesh.skeleton) {
          const rot = frame[name];
          if (rot) {
            mesh.skeleton.bones.forEach((bone, i) => {
              if (rot[i]) bone.rotation.set(rot[i].x, rot[i].y, rot[i].z);
            });
          }
        }
      });
    }
  }
}
